import type { ISearchCardV6 } from '~/models/SearchCardV6';
import type { HugeiconsProps } from '@hugeicons/react-native';
import {
  Activity03Icon,
  ApproximatelyEqualCircleIcon,
  ApproximatelyEqualSquareIcon,
  BedDoubleIcon,
  BubbleChatFavouriteIcon,
  EarthIcon,
  FavouriteIcon,
  FilterIcon,
  Fire02Icon,
  FolderLockedIcon,
  Image02Icon,
  LanguageSkillIcon,
  LocationUser02Icon,
  Orbit01Icon,
  SparklesIcon,
  StarIcon,
  Tag01Icon,
  TapeMeasureIcon,
  Target01Icon,
  Wifi01Icon,
} from '@hugeicons-pro/core-stroke-rounded';
export type RangeField = 'Age' | 'BMI' | 'Weight' | 'Height';

export const MEMBER_SEARCH_RANGE_FIELDS: RangeField[] = ['Age', 'BMI', 'Weight', 'Height'];

export const MEMBER_SEARCH_RANGE_FALLBACKS: Record<RangeField, [number, number]> = {
  Age: [18, 75],
  BMI: [16, 70],
  Weight: [80, 600],
  Height: [147, 203], // 4'10" to 6'8"
};

export type MultiSelectField = keyof Pick<
  ISearchCardV6,
  | 'SocialTags'
  | 'Genders'
  | 'InterestedIn'
  | 'Relationships'
  | 'SexualInterests'
  | 'LookingFor'
  | 'BodyTypes'
  | 'BodyPartsLiked'
  | 'BodyHair'
  | 'FacialHair'
  | 'CommunityKinks'
  | 'Kinks'
  | 'Languages'
  | 'Ethnicities'
>;

export const MEMBER_SEARCH_MULTI_SELECT_FIELDS: { field: MultiSelectField; labelKey: string }[] = [
  { field: 'SocialTags', labelKey: 'profile.identifyAs' },
  { field: 'Genders', labelKey: 'profile.gender' },
  { field: 'InterestedIn', labelKey: 'profile.interestedIn' },
  { field: 'SexualInterests', labelKey: 'profile.sexualInterests' },
  { field: 'Relationships', labelKey: 'profile.relationshipStatus' },
  { field: 'LookingFor', labelKey: 'profile.lookingFor' },
  { field: 'BodyTypes', labelKey: 'profile.bodyTypes' },
  { field: 'BodyHair', labelKey: 'profile.bodyHair' },
  { field: 'FacialHair', labelKey: 'profile.facialHair' },
  { field: 'CommunityKinks', labelKey: 'profile.communityKinks' },
  { field: 'Kinks', labelKey: 'profile.otherKinks' },
  { field: 'Languages', labelKey: 'profile.languages' },
  { field: 'Ethnicities', labelKey: 'profile.origin' },
];

export const MULTI_SELECT_LABEL_KEY_BY_FIELD: Partial<Record<MultiSelectField, string>> =
  MEMBER_SEARCH_MULTI_SELECT_FIELDS.reduce(
    (acc, { field, labelKey }) => {
      acc[field] = labelKey;
      return acc;
    },
    {} as Partial<Record<MultiSelectField, string>>
  );

export const MEMBER_SEARCH_MULTI_SELECT_SECTIONS: {
  key: string;
  titleKey: string;
  fallbackTitle: string;
  fields: MultiSelectField[];
}[] = [
  {
    key: 'identity',
    titleKey: 'profile.sections.identity',
    fallbackTitle: 'Identity & Background',
    fields: ['SocialTags', 'Genders', 'InterestedIn', 'Relationships', 'Languages', 'Ethnicities'],
  },
  {
    key: 'interestsAndKinks',
    titleKey: 'profile.sections.interestsAndKinks',
    fallbackTitle: 'Interests and kinks',
    fields: ['LookingFor', 'SexualInterests', 'CommunityKinks', 'Kinks'],
  },
  {
    key: 'bodyTypesAndFeatures',
    titleKey: 'profile.sections.bodyFeatures',
    fallbackTitle: 'Body types and features',
    fields: ['BodyTypes', 'BodyHair', 'FacialHair'],
  },
];

export type BooleanFilterField =
  | 'HasPhotos'
  | 'HasPrivatePhotos'
  | 'IsOnline'
  | 'IsActive'
  | 'IsNew'
  | 'IsFollower'
  | 'ImFollowing'
  | 'ImTheirBodyType';

export const MEMBER_SEARCH_BOOLEAN_FILTER_FIELDS: { field: BooleanFilterField; labelKey: string }[] =
  [
    { field: 'HasPhotos', labelKey: 'search.hasPhotos' },
    { field: 'HasPrivatePhotos', labelKey: 'search.hasPrivatePhotos' },
    { field: 'IsOnline', labelKey: 'search.isOnline' },
    { field: 'IsActive', labelKey: 'search.isActive' },
    { field: 'IsNew', labelKey: 'search.isNew' },
    { field: 'IsFollower', labelKey: 'search.isFollower' },
    { field: 'ImFollowing', labelKey: 'search.imFollowing' },
    { field: 'ImTheirBodyType', labelKey: 'search.imTheirBodyType' },
  ];

export const DEFAULT_MULTI_SELECT_ICON: HugeiconsProps['icon'] = Tag01Icon;
export const DEFAULT_BOOLEAN_ICON: HugeiconsProps['icon'] = FilterIcon;

export const MEMBER_SEARCH_MULTI_SELECT_ICON_BY_FIELD: Partial<
  Record<MultiSelectField, HugeiconsProps['icon']>
> = {
  SocialTags: Tag01Icon,
  Genders: LocationUser02Icon,
  InterestedIn: LocationUser02Icon,
  Relationships: FavouriteIcon,
  LookingFor: Target01Icon,
  SexualInterests: BedDoubleIcon,
  CommunityKinks: BubbleChatFavouriteIcon,
  Kinks: Fire02Icon,
  BodyTypes: TapeMeasureIcon,
  BodyHair: ApproximatelyEqualSquareIcon,
  FacialHair: ApproximatelyEqualCircleIcon,
  Languages: LanguageSkillIcon,
  Ethnicities: EarthIcon,
};

export const MEMBER_SEARCH_BOOLEAN_ICON_BY_FIELD: Partial<Record<BooleanFilterField, HugeiconsProps['icon']>> =
  {
    HasPhotos: Image02Icon,
    HasPrivatePhotos: FolderLockedIcon,
    IsOnline: Wifi01Icon,
    IsActive: Activity03Icon,
    IsNew: SparklesIcon,
    IsFollower: Orbit01Icon,
    ImFollowing: StarIcon,
    ImTheirBodyType: TapeMeasureIcon,
  };

